/**
 * Team Form — /office/settings/team/invite/ and /office/settings/team/<id>/edit/
 *
 * Initializes:
 *
 *   * intl-tel-input on phone fields (``initAllPhoneInputs``).
 *   * Role-based section toggling (cleaner card, manager perms, phone
 *     required marker) — driven by ``#id_role`` value.
 *   * Worker-type toggling (interim / subcontractor / independent
 *     blocks) — driven by ``[data-worker-type-toggle]`` (rendered by
 *     ``UserInviteForm``/``UserEditForm`` on the worker_type select).
 *     Switching away from a section clears its inputs so a stale value
 *     cannot reach the server (defence in depth — the form ``clean()``
 *     also rejects it).
 *   * Photo upload preview + 2 MB / image-MIME guards + remove button
 *     (with hidden ``photo-clear`` flag).
 *   * Initials sync — when no photo is shown, the avatar initials track
 *     the first/last name inputs.
 *
 * Submission, validation and submit-button protection are 100% server-
 * driven:
 *
 *   * Submit goes through HTMX (hx-boost on #main-content).
 *   * Django validates (UserInviteForm.clean_phone uses the same
 *     ``phonenumbers`` library that intl-tel-input uses client-side, so
 *     format checks are equivalent).
 *   * Errors come back rendered as ``.field-group.has-error`` blocks; the
 *     global ``form-error-scroll`` helper scrolls the first one into
 *     view and focuses it after ``htmx:afterSwap``.
 *   * The submit button spinner is locked/unlocked by the global
 *     ``data-protect-submit`` machinery in ``utils/forms/submit-button.js``.
 *
 * No client-side ``submit`` handler here on purpose — every previous
 * iteration that intercepted submit caused spinner-state inconsistencies
 * relative to other forms (e.g. Checklist Templates) that don't validate
 * client-side.
 *
 * Template contract on the ``<form data-auto-init="teamForm">``:
 *
 *   data-team-file-too-large="..."  — alert text when photo > 2 MB.
 *   data-team-not-image="..."       — alert text when MIME isn't image/*.
 *
 * Both default to English if absent.
 */

import { initAllPhoneInputs } from '../utils/forms/phone-input.js';

const PHOTO_MAX_BYTES = 2 * 1024 * 1024;

function _initRoleToggle(formEl) {
    const roleSelect = formEl.querySelector('#id_role');
    const cleanerFields = formEl.querySelector('#team-cleaner-fields');
    const managerPerms = formEl.querySelector('#team-manager-permissions');
    const phoneStar = formEl.querySelector('#team-phone-required-star');

    function currentRole() {
        if (roleSelect) return roleSelect.value;
        const hidden = formEl.querySelector('input[name="role"]');
        return hidden ? hidden.value : '';
    }

    function toggle() {
        const role = currentRole();
        if (cleanerFields) cleanerFields.classList.toggle('hidden', role !== 'cleaner');
        if (managerPerms) managerPerms.classList.toggle('hidden', role !== 'manager');
        if (phoneStar) phoneStar.classList.toggle('hidden', role !== 'cleaner');
    }

    if (roleSelect) roleSelect.addEventListener('change', toggle);
    toggle();
}

// Tab-driven layout (dynamic modal). Two visible tabs at a time: the
// always-present "General Information" plus a role-specific second tab —
// "Cleaner Details" for cleaners, "Permissions" for managers, none for
// owners. The panels (general / #team-cleaner-fields /
// #team-manager-permissions) are mutually exclusive here: visibility is
// owned by the active tab, NOT by the role (unlike ``_initRoleToggle``,
// which is the full-page two-column layout). Role only gates which second
// tab button is offered and prevents leaving a hidden tab active.
// Mirrors the customer-detail tab pattern (templates/office/customers/
// detail.html): the inactive state carries ``hover:border-gray-300`` so
// hovering a non-selected tab reveals the underline, matching the rest of
// the app's tabbed surfaces.
const TAB_ACTIVE_CLASS = 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500';
const TAB_INACTIVE_CLASS = 'border-transparent text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300';

function _initModalTabs(formEl) {
    const buttons = Array.prototype.slice.call(formEl.querySelectorAll('[data-team-tab]'));
    if (!buttons.length) return;

    const roleSelect = formEl.querySelector('#id_role');
    const phoneStar = formEl.querySelector('#team-phone-required-star');

    const panels = {
        general: formEl.querySelector('[data-team-panel="general"]'),
        cleaner: formEl.querySelector('[data-team-panel="cleaner"]'),
        permissions: formEl.querySelector('[data-team-panel="permissions"]'),
    };
    const btnByName = {};
    buttons.forEach(function (b) { btnByName[b.dataset.teamTab] = b; });

    function currentRole() {
        if (roleSelect) return roleSelect.value;
        const hidden = formEl.querySelector('input[name="role"]');
        return hidden ? hidden.value : '';
    }

    function secondTabFor(role) {
        if (role === 'cleaner') return 'cleaner';
        if (role === 'manager') return 'permissions';
        return null;
    }

    let activeName = 'general';

    function activate(name) {
        if (!panels[name]) name = 'general';
        activeName = name;
        buttons.forEach(function (b) {
            const on = b.dataset.teamTab === name;
            b.className = 'team-tab-btn px-4 py-2 text-sm font-medium border-b-2 '
                + (on ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS)
                + (b.classList.contains('hidden') ? ' hidden' : '');
        });
        Object.keys(panels).forEach(function (key) {
            if (panels[key]) panels[key].classList.toggle('hidden', key !== name);
        });
    }

    function syncRole() {
        const role = currentRole();
        const second = secondTabFor(role);
        if (btnByName.cleaner) btnByName.cleaner.classList.toggle('hidden', second !== 'cleaner');
        if (btnByName.permissions) btnByName.permissions.classList.toggle('hidden', second !== 'permissions');
        if (phoneStar) phoneStar.classList.toggle('hidden', role !== 'cleaner');
        // If the active tab is no longer offered for this role, fall back
        // to the always-present General tab.
        if (activeName !== 'general' && activeName !== second) {
            activate('general');
        } else {
            activate(activeName);
        }
    }

    buttons.forEach(function (b) {
        if (b._teamTabBound) return;
        b._teamTabBound = true;
        b.addEventListener('click', function () {
            if (b.classList.contains('hidden')) return;
            activate(b.dataset.teamTab);
        });
    });

    if (roleSelect && !roleSelect._teamRoleTabBound) {
        roleSelect._teamRoleTabBound = true;
        roleSelect.addEventListener('change', syncRole);
    }

    // Open-on-error: a 422 re-render with field errors in a role-specific
    // panel should land on that tab so the error is never hidden.
    let initial = 'general';
    const firstError = formEl.querySelector('[data-team-panel] .field-error');
    if (firstError) {
        const panel = firstError.closest('[data-team-panel]');
        if (panel && panel.dataset.teamPanel) initial = panel.dataset.teamPanel;
    }
    activeName = initial;
    syncRole();
}

function _initWorkerTypeToggle(formEl) {
    const workerTypeSelect = formEl.querySelector('[data-worker-type-toggle]');
    const interimBlock = formEl.querySelector('[data-interim-fields]');
    const subcontractorBlock = formEl.querySelector('[data-subcontractor-fields]');
    const independentBlock = formEl.querySelector('[data-independent-fields]');

    if (!workerTypeSelect && !interimBlock && !subcontractorBlock && !independentBlock) {
        return;
    }

    function clearBlockInputs(block) {
        if (!block) return;
        block.querySelectorAll('input, select').forEach(function (el) {
            if (el.tagName === 'SELECT') {
                el.selectedIndex = 0;
            } else if (el.type !== 'hidden') {
                el.value = '';
            }
        });
    }

    function toggle() {
        const value = workerTypeSelect ? workerTypeSelect.value : 'employee';
        const isInterim = value === 'interim';
        const isSubcontractor = value === 'subcontractor';
        const isIndependent = value === 'independent';

        if (interimBlock) {
            interimBlock.classList.toggle('hidden', !isInterim);
            if (!isInterim) clearBlockInputs(interimBlock);
        }
        if (subcontractorBlock) {
            subcontractorBlock.classList.toggle('hidden', !isSubcontractor);
            if (!isSubcontractor) clearBlockInputs(subcontractorBlock);
        }
        if (independentBlock) {
            independentBlock.classList.toggle('hidden', !isIndependent);
            if (!isIndependent) clearBlockInputs(independentBlock);
        }
    }

    if (workerTypeSelect) workerTypeSelect.addEventListener('change', toggle);
    toggle();
}

function _initPhotoUpload(formEl) {
    const photoInput = formEl.querySelector('#team-photo-input');
    const photoPreview = formEl.querySelector('#team-photo-preview');
    const photoInitials = formEl.querySelector('#team-photo-initials');
    const removePhotoBtn = formEl.querySelector('#team-remove-photo-btn');
    const photoClearInput = formEl.querySelector('#team-photo-clear-input');

    const fileTooLargeMsg = formEl.dataset.teamFileTooLarge
        || 'File too large. Maximum size is 2MB.';
    const notImageMsg = formEl.dataset.teamNotImage
        || 'Please select an image file.';

    if (photoInput) {
        photoInput.addEventListener('change', function () {
            const file = this.files && this.files[0];
            if (!file) return;
            if (file.size > PHOTO_MAX_BYTES) {
                window.alert(fileTooLargeMsg);
                this.value = '';
                return;
            }
            if (!file.type.startsWith('image/')) {
                window.alert(notImageMsg);
                this.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onload = function (e) {
                if (photoPreview) {
                    photoPreview.src = e.target.result;
                    photoPreview.classList.remove('hidden');
                }
                if (photoInitials) photoInitials.classList.add('hidden');
                if (photoClearInput) photoClearInput.value = '';
                if (removePhotoBtn) removePhotoBtn.classList.remove('hidden');
            };
            reader.readAsDataURL(file);
        });
    }

    if (removePhotoBtn) {
        removePhotoBtn.addEventListener('click', function () {
            if (photoPreview) {
                photoPreview.src = '';
                photoPreview.classList.add('hidden');
            }
            if (photoInitials) photoInitials.classList.remove('hidden');
            if (photoInput) photoInput.value = '';
            if (photoClearInput) photoClearInput.value = 'true';
            this.classList.add('hidden');
        });
    }
}

function _initInitialsSync(formEl) {
    const photoPreview = formEl.querySelector('#team-photo-preview');
    const photoInitials = formEl.querySelector('#team-photo-initials');
    const firstNameInput = formEl.querySelector('#id_first_name');
    const lastNameInput = formEl.querySelector('#id_last_name');

    if (!photoInitials) return;

    function update() {
        if (photoPreview && !photoPreview.classList.contains('hidden')) return;
        const first = (firstNameInput ? firstNameInput.value : '').trim();
        const last = (lastNameInput ? lastNameInput.value : '').trim();
        photoInitials.textContent = (first || last)
            ? (first.charAt(0) + last.charAt(0)).toUpperCase()
            : '?';
    }

    if (firstNameInput) firstNameInput.addEventListener('input', update);
    if (lastNameInput) lastNameInput.addEventListener('input', update);
}

export function initTeamForm(config) {
    config = config || {};
    const formEl = config.formEl || document.querySelector('form[data-auto-init="teamForm"]');
    if (!formEl) return;
    initAllPhoneInputs(formEl);
    // The dynamic-modal layout ships tab buttons; the full-page invite
    // form does not. Tabs own panel visibility there, so the two-column
    // role toggle would fight with them — pick exactly one.
    if (formEl.querySelector('[data-team-tab]')) {
        _initModalTabs(formEl);
    } else {
        _initRoleToggle(formEl);
    }
    _initWorkerTypeToggle(formEl);
    _initPhotoUpload(formEl);
    _initInitialsSync(formEl);
}

export function autoInitTeamForm() {
    const formEl = document.querySelector('form[data-auto-init="teamForm"]');
    if (!formEl) return;
    initTeamForm({ formEl: formEl });
}
